import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { fallanalyseAPI, sammelfallAPI } from '@/lib/api';
import { toast } from 'sonner';
import { trackProjektEntwurf } from '@/lib/tracking';
import { StepEingabe } from '@/components/fallanalyse/StepEingabe';
import { StepErkannt } from '@/components/fallanalyse/StepErkannt';
import { StepEinschaetzung } from '@/components/fallanalyse/StepEinschaetzung';
import { StepWeiter } from '@/components/fallanalyse/StepWeiter';
import { StepAssistent } from '@/components/fallanalyse/StepAssistent';
import { EingabeKontext } from '@/components/fallanalyse/EingabeKontext';
import { StepFreigabe } from '@/components/fallanalyse/StepFreigabe';
import { useWizardDraft } from '@/hooks/useWizardDraft';
import {
  trackSchnellcheckEingabe,
  trackSchnellcheckErkannt,
  trackSchnellcheckEinschaetzung,
  trackSchnellcheckNaechsterSchritt,
  trackSchnellcheckCtaClick,
  trackAppSchnellcheckStart,
  trackAppSchnellcheckComplete,
  captureEntrySource,
} from '@/lib/tracking';
import { TreeDeciduous, RotateCcw, Save, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const STEP_KEYS = ['schnellcheck.step1', 'schnellcheck.step2', 'schnellcheck.step3', 'schnellcheck.step4', 'schnellcheck.step5'];

export default function SchnellCheckPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [freitext, setFreitext] = useState('');
  const [loading, setLoading] = useState(false);
  const [ansicht, setAnsicht] = useState(null);
  const [plausibilitaet, setPlausibilitaet] = useState(null);

  // Zielgruppe aus URL-Parameter lesen (z.B. ?zielgruppe=fbg)
  const validZielgruppen = ['waldbesitzer', 'fbg', 'berater', 'kommune', 'unsicher'];
  const urlZielgruppe = searchParams.get('zielgruppe');
  const [zielgruppe, setZielgruppe] = useState(
    validZielgruppen.includes(urlZielgruppe) ? urlZielgruppe : 'waldbesitzer'
  );

  // Fallart aus URL-Parameter lesen (z.B. ?fallart=waldwert)
  const validFallarten = ['waldwert', 'kaufen', 'verkaufen', 'geerbt', 'schaden', 'pflege', 'wild', 'unsicher'];
  const urlFallart = searchParams.get('fallart');
  const [fallart, setFallart] = useState(
    validFallarten.includes(urlFallart) ? urlFallart : null
  );

  const { hasDraft, draftLoaded, saveDraft, loadDraft, clearDraft, dismissDraft } = useWizardDraft('schnellcheck');

  // UTM-Parameter beim Einstieg erfassen
  useEffect(() => {
    captureEntrySource();
  }, []);

  // Auto-save on step/data changes
  useEffect(() => {
    if (step > 1 || freitext.length > 10) {
      saveDraft({ _step: step, freitext, zielgruppe, fallart, ansicht });
    }
  }, [step, freitext, zielgruppe, fallart, ansicht, saveDraft]);

  // Beforeunload warning
  useEffect(() => {
    const handler = (e) => {
      if (step > 1 && ansicht) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [step, ansicht]);

  const resumeDraft = () => {
    const draft = loadDraft();
    if (draft) {
      setStep(draft._step || 1);
      setFreitext(draft.freitext || '');
      setZielgruppe(draft.zielgruppe || 'waldbesitzer');
      setFallart(draft.fallart || null);
      setAnsicht(draft.ansicht || null);
      toast.success(t('toasts.fortschrittWiederhergestellt'));
    }
  };

  const analyseStarten = async (text) => {
    if (!text || text.trim().length < 10) {
      toast.error(t('schnellcheck.inputTooShort'));
      return;
    }
    setLoading(true);
    setPlausibilitaet(null);
    trackSchnellcheckEingabe('freitext');
    trackAppSchnellcheckStart(fallart);
    try {
      const res = await fallanalyseAPI.nutzeransicht(text.trim(), null, zielgruppe);
      if (res.data.status && res.data.status !== 'ok') {
        setPlausibilitaet(res.data.plausibilitaet);
        setAnsicht(null);
        setStep(1);
      } else {
        setAnsicht(res.data);
        setPlausibilitaet(null);
        setStep(2);
        const falltyp = res.data?.erkannt?.erkannte_merkmale?.find(m => m.label === 'Maßnahme');
        trackSchnellcheckErkannt(falltyp?.wert || 'unbekannt');
        trackAppSchnellcheckComplete(falltyp?.wert || 'unbekannt', fallart);
      }
    } catch {
      toast.error(t('schnellcheck.analyseFehler'));
    } finally {
      setLoading(false);
    }
  };

  const ergaenzenUndNeuAnalysieren = async (zusatzText) => {
    const neuerText = freitext + '\n' + zusatzText;
    setFreitext(neuerText);
    await analyseStarten(neuerText);
  };

  const neuStarten = () => {
    setStep(1);
    setFreitext('');
    setAnsicht(null);
    setPlausibilitaet(null);
    setZielgruppe('waldbesitzer');
    clearDraft();
  };

  const goToStep3 = () => {
    trackSchnellcheckEinschaetzung(ansicht?.empfehlung?.empfohlener_weg || '');
    setStep(3);
  };

  const goToStep4 = () => {
    trackSchnellcheckNaechsterSchritt(ansicht?.naechster_schritt?.aktion_typ || '');
    setStep(4);
  };

  const goToStep5 = () => {
    setStep(5);
  };

  const handleReanalyse = async (neuerText, neueZielgruppe) => {
    setFreitext(neuerText);
    setZielgruppe(neueZielgruppe);
    setLoading(true);
    setPlausibilitaet(null);
    try {
      const res = await fallanalyseAPI.nutzeransicht(neuerText.trim(), null, neueZielgruppe);
      if (res.data.status && res.data.status !== 'ok') {
        setPlausibilitaet(res.data.plausibilitaet);
        setAnsicht(null);
        setStep(1);
      } else {
        setAnsicht(res.data);
        setPlausibilitaet(null);
        setStep(2);
        const falltyp = res.data?.erkannt?.erkannte_merkmale?.find(m => m.label === 'Maßnahme');
        trackSchnellcheckErkannt(falltyp?.wert || 'reanalyse');
      }
    } catch {
      toast.error(t('schnellcheck.analyseFehler'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = () => {
    trackSchnellcheckCtaClick('registrieren');
    const token = localStorage.getItem('token');
    navigate(token ? '/dashboard' : '/registrieren');
  };

  const handleSammelfallErstellen = async () => {
    trackSchnellcheckCtaClick('sammelfall_buendeln');
    try {
      const res = await sammelfallAPI.erstellen(freitext, 'fbg', user?.id || null);
      navigate(`/sammelfall/${res.data.sammelfall_id}`);
    } catch {
      toast.error(t('toasts.sammelfallFehler'));
    }
  };

  const handleProjektEntwurf = async () => {
    if (!ansicht) return;
    trackSchnellcheckCtaClick('projekt_entwurf');
    try {
      const res = await fallanalyseAPI.projektEntwurf(freitext, zielgruppe, ansicht);
      trackProjektEntwurf(res.data.entwurf_id);
      toast.success(t('toasts.entwurfGespeichert'));
      const token = localStorage.getItem('token');
      if (token) {
        // Entwurf dem eingeloggten User zuordnen
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const userId = payload.user_id || payload.sub;
          if (userId) {
            await fallanalyseAPI.projektEntwurfZuordnen(res.data.entwurf_id, userId);
          }
        } catch {
          // Token-Parsing fehlgeschlagen – nicht kritisch
        }
        navigate('/dashboard');
      } else {
        navigate(`/registrieren?entwurf=${res.data.entwurf_id}`);
      }
    } catch {
      toast.error(t('toasts.fallSpeichernFehler'));
      const token = localStorage.getItem('token');
      navigate(token ? '/dashboard' : '/registrieren');
    }
  };

  return (
    <div className="min-h-screen bg-warm-50">
      {/* Minimal Header */}
      <header className="border-b border-warm-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-primary font-bold" data-testid="logo-link">
            <TreeDeciduous className="h-5 w-5" />
            <span className="font-serif text-lg">ForestHub</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/anmelden"
              className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
              data-testid="login-link"
            >
              {t('nav.login')}
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Stepper */}
        <nav className="mb-6" aria-label="Fortschritt" data-testid="schnellcheck-stepper">
          <ol className="flex items-center gap-0">
            {STEP_KEYS.map((key, i) => {
              const nr = i + 1;
              const done = step > nr;
              const active = step === nr;
              return (
                <li key={nr} className="flex items-center flex-1 last:flex-none">
                  <button
                    disabled={nr > step}
                    onClick={() => nr <= step && setStep(nr)}
                    className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                      active ? 'text-primary' : done ? 'text-primary/60 hover:text-primary' : 'text-gray-400'
                    }`}
                    data-testid={`sc-step-${nr}-btn`}
                  >
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                      active ? 'bg-primary text-white border-primary' :
                      done ? 'bg-primary/10 text-primary border-primary/30' :
                      'bg-gray-100 text-gray-400 border-gray-200'
                    }`}>
                      {done ? '\u2713' : nr}
                    </span>
                    <span className="hidden sm:inline">{t(key)}</span>
                  </button>
                  {i < STEP_KEYS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-3 rounded ${done ? 'bg-primary/30' : 'bg-gray-200'}`} />
                  )}
                </li>
              );
            })}
          </ol>
          <p className="text-xs text-muted-foreground mt-2.5 text-center" data-testid="schnellcheck-step-label">
            {t('schnellcheck.schrittVon')} {step} {t('schnellcheck.schrittVonSuffix')}
          </p>
        </nav>

        {/* Resume Draft Dialog */}
        {hasDraft && draftLoaded && step === 1 && !ansicht && (
          <Card className="mb-6 border-primary/20 bg-primary/[0.03]" data-testid="resume-draft-dialog">
            <CardContent className="p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Save className="h-5 w-5 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Gespeicherter Fortschritt gefunden</p>
                  <p className="text-xs text-gray-500">Sie hatten bereits eine Analyse begonnen. Möchten Sie fortfahren?</p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" onClick={resumeDraft} data-testid="resume-draft-btn">
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  {t('schnellcheck.draftFortfahrenBtn')}
                </Button>
                <Button size="sm" variant="outline" onClick={dismissDraft} data-testid="dismiss-draft-btn">
                  {t('schnellcheck.draftVerwerfenBtn')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* EingabeKontext – sichtbar in Steps 2-5, erlaubt Bearbeitung ohne Reset */}
        {step >= 2 && ansicht && (
          <EingabeKontext
            freitext={freitext}
            zielgruppe={zielgruppe}
            zusammenfassung={ansicht?.erkannt?.zusammenfassung_text}
            onReanalyse={handleReanalyse}
            loading={loading}
            t={t}
          />
        )}

        {step === 1 && !plausibilitaet && (
          <StepEingabe
            freitext={freitext}
            setFreitext={setFreitext}
            onSubmit={analyseStarten}
            loading={loading}
            isPublic={true}
            zielgruppe={zielgruppe}
            onZielgruppeChange={setZielgruppe}
            t={t}
          />
        )}
        {step === 1 && plausibilitaet && (
          <StepAssistent
            ansicht={{ assistenz: plausibilitaet }}
            onErgaenzen={(text) => { setFreitext(text); analyseStarten(text); }}
            onZurueck={() => setPlausibilitaet(null)}
            loading={loading}
            t={t}
          />
        )}
        {step === 2 && ansicht && (
          <StepErkannt
            ansicht={ansicht}
            onWeiter={goToStep3}
            onErgaenzen={ergaenzenUndNeuAnalysieren}
            onZurueck={() => setStep(1)}
            loading={loading}
            t={t}
          />
        )}
        {step === 3 && ansicht && (
          <StepEinschaetzung
            ansicht={ansicht}
            onWeiter={goToStep4}
            onZurueck={() => setStep(2)}
            onRegister={handleRegister}
            onProjektEntwurf={handleProjektEntwurf}
            isPublic={true}
            t={t}
          />
        )}
        {step === 4 && ansicht && (
          <StepWeiter
            ansicht={ansicht}
            onNeustart={neuStarten}
            onProjektErstellen={handleRegister}
            isPublic={true}
            onRegister={handleRegister}
            onZurueck={() => setStep(3)}
            onSammelfallBuendeln={zielgruppe === 'fbg' ? handleSammelfallErstellen : null}
            onDienstleisterProfil={zielgruppe === 'dienstleister' ? () => navigate('/fuer-dienstleister') : null}
            onProjektEntwurf={handleProjektEntwurf}
            t={t}
          />
        )}
        {step === 5 && ansicht && (
          <StepFreigabe
            ansicht={ansicht}
            freitext={freitext}
            zielgruppe={zielgruppe}
            onZurueck={() => setStep(4)}
            onFreigeben={async () => {
              await handleProjektEntwurf();
              clearDraft();
            }}
            onRegister={handleRegister}
            t={t}
          />
        )}
      </main>

      {/* Minimal Footer */}
      <footer className="border-t border-warm-200 bg-white mt-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between text-xs text-muted-foreground">
          <span>{t('schnellcheck.footerText')}</span>
          <div className="flex gap-4">
            <Link to="/datenschutz" className="hover:text-foreground transition-colors">{t('footer.privacy')}</Link>
            <Link to="/impressum" className="hover:text-foreground transition-colors">{t('footer.imprint')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
